// PARAQTA — Wrapped (Stage 6.9.5).
// Жылдық оқу қорытынды — Spotify Wrapped pattern.
// Premium gate: Free user → lock modal. Жан дос → 6 immersive slide.
// Бот тимеу: тек READ (/api/wrapped/{year}).

import { api } from '../../core/api.js';
import { show } from '../../core/router.js';

let _state = {
  data: null,
  slide: 0,
  year: 0,
  loading: false,
};

const TOTAL_SLIDES = 6;

function esc(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

function fmtNum(n) {
  return Number(n || 0).toLocaleString('kk-KZ').replace(/,/g, ' ');
}

const root = () => document.getElementById('pg-wrapped');

export async function loadWrapped() {
  const r = root();
  if (!r) return;
  if (_state.loading) return;
  _state.loading = true;

  r.innerHTML = '<div class="w-loading">Жылдық есеп дайындалуда…</div>';

  const year = new Date().getFullYear();
  _state.year = year;
  _state.slide = 0;

  try {
    const data = await api(`/api/wrapped/${year}`);
    if (data?.status === 'premium_required') {
      const { showPremiumLockModal } = await import('../premium-lock/modal.js');
      showPremiumLockModal('wrapped');
      try { show('profile'); } catch (_) { window.location.hash = '#profile'; }
      return;
    }
    if (data?.status === 'invalid_year') {
      r.innerHTML = '<div class="w-error">Жыл қолжетімсіз</div>';
      return;
    }
    if (data?.status !== 'ok' || !data.wrapped) {
      r.innerHTML = '<div class="w-error">Қате — қайта көрейік</div>';
      return;
    }
    _state.data = data.wrapped;
    renderSlide();
  } catch (e) {
    console.error('wrapped load failed', e);
    r.innerHTML = '<div class="w-error">Қате — қайта көрейік</div>';
  } finally {
    _state.loading = false;
  }
}

function renderSlide() {
  const r = root();
  if (!r || !_state.data) return;
  const slides = [
    renderWelcome,
    renderTotals,
    renderTopBooks,
    renderGenres,
    renderStreak,
    renderFinal,
  ];
  const fn = slides[_state.slide];
  if (!fn) return;

  const progressBars = Array.from({ length: TOTAL_SLIDES }, (_, i) =>
    `<div class="w-progress-bar${i <= _state.slide ? ' active' : ''}"></div>`
  ).join('');

  r.innerHTML = `
    <div class="w-page">
      <div class="w-progress">${progressBars}</div>
      <button class="w-close" data-action="wrapped-close" aria-label="Жабу">
        <i class="ti ti-x"></i>
      </button>
      <!-- Stage 7.5: PNG сақтау батырмасы (iPhone "Сурет сақтау" арқылы) -->
      <button class="w-save-png" data-action="wrapped-share-png" aria-label="Сурет сақтау" title="Сурет сақтау">
        <i class="ti ti-download"></i>
      </button>
      <div class="w-slide">${fn(_state.data)}</div>
      ${_state.slide > 0
        ? `<button class="w-prev" data-action="wrapped-prev" aria-label="Алдыңғы">
             <i class="ti ti-chevron-left"></i>
           </button>` : ''}
      ${_state.slide < TOTAL_SLIDES - 1
        ? `<button class="w-next" data-action="wrapped-next" aria-label="Келесі">
             <i class="ti ti-chevron-right"></i>
           </button>` : ''}
    </div>
  `;
  bindEvents();
}

function bindEvents() {
  const r = root();
  if (!r) return;
  // Touch swipe (mobile)
  let startX = null;
  const slide = r.querySelector('.w-slide');
  if (slide) {
    slide.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
    }, { passive: true });
    slide.addEventListener('touchend', (e) => {
      if (startX == null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) {
        if (dx < 0 && _state.slide < TOTAL_SLIDES - 1) {
          _state.slide++;
          renderSlide();
        } else if (dx > 0 && _state.slide > 0) {
          _state.slide--;
          renderSlide();
        }
      }
      startX = null;
    }, { passive: true });
  }
  // Keyboard
  document.addEventListener('keydown', onKey);
}

function onKey(e) {
  const r = root();
  if (!r || !r.classList.contains('on')) {
    document.removeEventListener('keydown', onKey);
    return;
  }
  if (e.key === 'ArrowRight' && _state.slide < TOTAL_SLIDES - 1) {
    _state.slide++;
    renderSlide();
  } else if (e.key === 'ArrowLeft' && _state.slide > 0) {
    _state.slide--;
    renderSlide();
  } else if (e.key === 'Escape') {
    wrappedClose();
  }
}

// ── Slides ─────────────────────────────────────────────────────

function renderWelcome(d) {
  return `
    <div class="w-content w-content-welcome">
      <div class="w-logo">PARAQTA</div>
      <h1 class="w-title-huge">${esc(String(_state.year))}</h1>
      <p class="w-subtitle">Жылдық оқу қорытынды</p>
      <p class="w-italic">«Бірге парақтадық.»</p>
      <button class="w-cta" data-action="wrapped-next">
        Бастау
        <i class="ti ti-arrow-right"></i>
      </button>
    </div>
  `;
}

function renderTotals(d) {
  return `
    <div class="w-content">
      <div class="w-eyebrow">САНДАРМЕН ${esc(String(_state.year))}</div>
      <div class="w-stats">
        <div class="w-stat">
          <div class="w-stat-num">${esc(String(d.books_count || 0))}</div>
          <div class="w-stat-label">кітап аяқтадың</div>
        </div>
        <div class="w-stat">
          <div class="w-stat-num">${esc(fmtNum(d.pages_total))}</div>
          <div class="w-stat-label">бет парақтадың</div>
        </div>
        <div class="w-stat">
          <div class="w-stat-num">${esc(String(d.best_streak || 0))} <span>🔥</span></div>
          <div class="w-stat-label">күн streak</div>
        </div>
      </div>
      <p class="w-italic">«Сен — оқырман.»</p>
    </div>
  `;
}

function renderTopBooks(d) {
  const list = Array.isArray(d.top_books) ? d.top_books : [];
  if (list.length === 0) {
    return `
      <div class="w-content">
        <div class="w-eyebrow">ТОП КІТАПТАР</div>
        <p class="w-italic" style="margin-top: 60px;">
          ${esc(String(_state.year))} жылы әлі кітап аяқтамадың.<br>
          Бір бөлімнен бастайық.
        </p>
      </div>
    `;
  }
  const cards = list.slice(0, 3).map((b, i) => {
    const cover = b.cover_url
      ? `<div class="w-book-cover" style="background-image:url('${esc(b.cover_url)}'); background-size:cover; background-position:center;"></div>`
      : `<div class="w-book-cover"><div class="w-book-cover-text">${esc(b.title || '')}</div></div>`;
    return `
      <div class="w-book">
        <div class="w-book-rank">${i + 1}</div>
        ${cover}
        <div class="w-book-info">
          <div class="w-book-title">${esc(b.title || '—')}</div>
          <div class="w-book-author">${esc(b.author || '')}</div>
          ${b.pages ? `<div class="w-book-pages">${esc(String(b.pages))} бет</div>` : ''}
        </div>
      </div>
    `;
  }).join('');
  return `
    <div class="w-content">
      <div class="w-eyebrow">ТОП ${list.length} КІТАП</div>
      <div class="w-books">${cards}</div>
      <p class="w-italic">«Жанымды толтырған кітаптар.»</p>
    </div>
  `;
}

function renderGenres(d) {
  const list = Array.isArray(d.genres) ? d.genres : [];
  if (list.length === 0) {
    return `
      <div class="w-content">
        <div class="w-eyebrow">МЕНІҢ ОҚУ СТИЛІМ</div>
        <p class="w-italic" style="margin-top: 60px;">Жанр data әзірге жоқ.</p>
      </div>
    `;
  }
  const total = list.reduce((s, g) => s + g.cnt, 0) || 1;
  const bars = list.map(g => {
    const pct = Math.round((g.cnt / total) * 100);
    return `
      <div class="w-genre">
        <div class="w-genre-bar" style="width:${pct}%"></div>
        <div class="w-genre-label">
          <span>${esc(g.genre)}</span>
          <span>${pct}%</span>
        </div>
      </div>
    `;
  }).join('');
  return `
    <div class="w-content">
      <div class="w-eyebrow">МЕНІҢ ОҚУ СТИЛІМ</div>
      <div class="w-genres">${bars}</div>
      <p class="w-italic">«Жанрды өзім тапқан.»</p>
    </div>
  `;
}

function renderStreak(d) {
  const monthly = Array.isArray(d.monthly) ? d.monthly : [];
  const maxCnt = monthly.reduce((m, x) => Math.max(m, x.cnt), 0) || 1;
  const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => {
    const item = monthly.find(x => x.month === m);
    const cnt = item ? item.cnt : 0;
    const h = Math.max(4, Math.round((cnt / maxCnt) * 100));
    return `
      <div class="w-month">
        <div class="w-month-bar" style="height:${h}%"></div>
        <div class="w-month-label">${m}</div>
      </div>
    `;
  }).join('');
  return `
    <div class="w-content">
      <div class="w-eyebrow">ЕҢ ҰЗАҚ STREAK</div>
      <div class="w-streak-huge">
        ${esc(String(d.best_streak || 0))} <span class="w-fire">🔥</span>
      </div>
      <div class="w-streak-label">күн қатарынан оқыдың</div>
      <div class="w-monthly">${months}</div>
      <p class="w-italic">«Сабырлы оқырман.»</p>
    </div>
  `;
}

function renderFinal(d) {
  return `
    <div class="w-content w-content-final">
      <div class="w-logo">PARAQTA</div>
      <h1 class="w-title">Менің ${esc(String(_state.year))}</h1>
      <div class="w-final-stats">
        <div><strong>${esc(String(d.books_count || 0))}</strong> кітап</div>
        <div><strong>${esc(fmtNum(d.pages_total))}</strong> бет</div>
        <div><strong>${esc(String(d.best_streak || 0))}</strong> күн streak</div>
      </div>
      <p class="w-italic">«Оқыған адам — ойлы адам.»</p>
      <div class="w-share">
        <button class="w-share-btn w-share-btn-tg" data-action="wrapped-share-telegram">
          <i class="ti ti-brand-telegram"></i>
          Telegram-да share
        </button>
        <button class="w-share-btn" data-action="wrapped-share-native">
          <i class="ti ti-share"></i>
          Басқа қосымшамен
        </button>
      </div>
      <div class="w-footer">paraqta.kz · #PARAQTA${esc(String(_state.year))}</div>
    </div>
  `;
}

// ── Actions ────────────────────────────────────────────────────

export function wrappedNext() {
  if (_state.slide < TOTAL_SLIDES - 1) {
    _state.slide++;
    renderSlide();
  }
}

export function wrappedPrev() {
  if (_state.slide > 0) {
    _state.slide--;
    renderSlide();
  }
}

export function wrappedClose() {
  document.removeEventListener('keydown', onKey);
  try { show('profile'); } catch (_) { window.location.hash = '#profile'; }
}

function shareText() {
  const d = _state.data || {};
  return `🪶 Менің PARAQTA ${_state.year} қорытынды:
${d.books_count || 0} кітап · ${fmtNum(d.pages_total)} бет · ${d.best_streak || 0} күн streak

paraqta.kz · #PARAQTA${_state.year}`;
}

export function wrappedShareTelegram() {
  const text = shareText();
  const url = `https://t.me/share/url?url=${encodeURIComponent('https://paraqta.kz')}&text=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener');
}

// Stage 7.5: Wrapped slide-ті PNG-ге сақтау (Web Share API → iOS "Сурет сақтау").
export async function wrappedSharePng() {
  const r = root();
  if (!r) return;
  const slide = r.querySelector('.w-slide');
  if (!slide) return;
  // toast import
  let toast = null;
  try { const m = await import('../../utils/toast.js'); toast = m.toast; } catch (_) {}
  if (toast) toast('📸 Сурет дайындалуда…');
  try {
    const mod = await import('html2canvas');
    const html2canvas = mod.default || mod;
    // Slide-тың computed background-ы (page back) — карта толық тура
    const cs = window.getComputedStyle(r.querySelector('.w-page') || r);
    const bg = cs.backgroundColor || '#15120E';
    const canvas = await html2canvas(slide, {
      backgroundColor: bg.includes('rgba(0, 0, 0, 0)') ? '#15120E' : bg,
      scale: 2,
      useCORS: true,
      logging: false,
      // Stage 7.5: PNG-да бөлісу батырмалары шықпасын (founder feedback)
      ignoreElements: (el) => {
        if (!el || !el.classList) return false;
        return el.classList.contains('w-share') ||
               el.classList.contains('w-share-btn') ||
               el.classList.contains('w-save-png') ||
               el.classList.contains('w-close') ||
               el.classList.contains('w-progress') ||
               el.classList.contains('w-prev') ||
               el.classList.contains('w-next');
      },
    });
    canvas.toBlob(async (blob) => {
      if (!blob) {
        if (toast) toast('Сурет жасалмады');
        return;
      }
      const file = new File([blob], `paraqta-wrapped-${_state.year || ''}.png`, { type: 'image/png' });
      // Web Share API → iOS Photos / Telegram / WhatsApp
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: `PARAQTA ${_state.year}`,
            text: 'Жыл қорытындысы — paraqta.kz',
          });
          if (toast) toast('Бөлісілді');
          return;
        } catch (_) { /* user cancel → fallthrough download */ }
      }
      // Desktop / fallback — жүктеу
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      if (toast) toast('Сурет жүктелді');
    }, 'image/png');
  } catch (e) {
    console.warn('wrappedSharePng failed', e);
    if (toast) toast('Сурет жасау сәтсіз');
  }
}

export async function wrappedShareNative() {
  const text = shareText();
  if (navigator.share) {
    try {
      await navigator.share({
        title: `PARAQTA ${_state.year}`,
        text,
        url: 'https://paraqta.kz',
      });
    } catch (_) { /* user cancel */ }
    return;
  }
  // Fallback — clipboard
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text + '\nhttps://paraqta.kz');
      const mod = await import('../../utils/toast.js').catch(() => null);
      if (mod?.toast) mod.toast('📋 Көшірілді — Telegram-ға қойыңыз');
      else alert('Көшірілді');
    } catch (_) {
      alert(text);
    }
  } else {
    alert(text);
  }
}
